import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  TrendingUp,
  ListChecks,
  AlertTriangle,
  Truck,
  Sparkles,
  ArrowRight,
  Activity,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppTile, { openAppNewTab } from "@/components/AppTile";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/timeago";

const KPI_DEFS = [
  {
    key: "active_waybills",
    label: "Active Waybills",
    icon: FileText,
    accent: "text-cargodash-primary-600",
    bg: "bg-cargodash-primary-50",
  },
  {
    key: "pending_tasks",
    label: "Pending Tasks",
    icon: ListChecks,
    accent: "text-cargodash-action",
    bg: "bg-orange-50",
  },
  {
    key: "open_issues",
    label: "Open Issues",
    icon: AlertTriangle,
    accent: "text-cargodash-alert-600",
    bg: "bg-red-50",
  },
  {
    key: "deliveries_today",
    label: "Deliveries Today",
    icon: Truck,
    accent: "text-slate-800",
    bg: "bg-slate-100",
  },
];

function KpiCard({ def, value, loading, isMock }) {
  const Icon = def.icon;
  return (
    <div
      data-testid={`kpi-${def.key}`}
      className="relative rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cargodash-primary-500/40 hover:shadow"
    >
      <div className="flex items-start justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${def.bg}`}>
          <Icon className={`h-4.5 w-4.5 ${def.accent}`} />
        </div>
        {isMock && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
            <Sparkles className="h-2.5 w-2.5" />
            demo
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {loading ? (
            <span className="inline-block h-7 w-12 animate-pulse rounded bg-slate-100" />
          ) : (
            value
          )}
        </div>
        <div className="mt-1 text-sm font-medium text-slate-600">{def.label}</div>
        {isMock && (
          <div className="mt-2 text-[11px] text-slate-400">
            Demo values · real data in Phase 3+
          </div>
        )}
      </div>
    </div>
  );
}

function compareApps(a, b) {
  const la = a.last_launched_at ? new Date(a.last_launched_at).getTime() : 0;
  const lb = b.last_launched_at ? new Date(b.last_launched_at).getTime() : 0;
  if (la !== lb) return lb - la;
  return a.name.localeCompare(b.name);
}

function formatActivity(r) {
  const actor = r.actor_email ? r.actor_email.split("@")[0] : "someone";
  const target = r.target_label ? ` ${r.target_label}` : "";
  const verbs = {
    "user.create": `created user${target}`,
    "user.update": `updated user${target}`,
    "user.disable": `disabled user${target}`,
    "user.enable": `enabled user${target}`,
    "user.reset_password": `reset password for${target}`,
    "user.delete": `deleted user${target}`,
    "role.create": `created role${target}`,
    "role.update": `updated role${target}`,
    "role.delete": `deleted role${target}`,
    "app.create": `registered app${target}`,
    "app.update": `updated app${target}`,
    "app.delete": `removed app${target}`,
    "matrix.update": `updated matrix row${target}`,
    "auth.login.success": `signed in`,
    "auth.login.failure": `failed to sign in${r.actor_email ? "" : ` (${r.metadata?.reason || "unknown"})`}`,
    "auth.logout": `signed out`,
    "sso.launch.success": `launched${target}`,
    "sso.launch.failure": `was blocked from${target || " an app"}`,
    "sso.key.rotate": `rotated the SSO signing key`,
  };
  return `${actor} ${verbs[r.action] || r.action}`;
}

const FEED_REFRESH_MS = 30000;

export default function Dashboard() {
  const { user, hasPerm, activeTenant } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [kpiSource, setKpiSource] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityScope, setActivityScope] = useState("self");
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);

  const canSeeCalendar = hasPerm("calendar.read");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [k, a] = await Promise.allSettled([
          api.get("/dashboard/kpis"),
          api.get("/apps/mine"),
        ]);
        if (!alive) return;
        if (k.status === "fulfilled") {
          setKpis(k.value.data.kpis);
          setKpiSource(k.value.data.source);
        }
        if (a.status === "fulfilled") {
          setApps(Array.isArray(a.value.data) ? a.value.data : []);
        } else {
          toast.error("Failed to load apps");
        }
      } finally {
        if (alive) {
          setKpiLoading(false);
          setAppsLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/reports/recent-activity?limit=10");
        if (!alive) return;
        setActivity(data.rows || []);
        setActivityScope(data.scope || "self");
      } catch (err) {
        // Polling endpoint — silent UI is intentional but we log so
        // repeated failures are greppable in devtools.
        console.error("dashboard: recent-activity fetch failed", err);
      }
    };
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, FEED_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (!canSeeCalendar) {
      setUpcomingLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const now = new Date();
        const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const qs = new URLSearchParams({
          start: now.toISOString(),
          end: end.toISOString(),
          resource_type: "vehicle",
        });
        const [ev, vv] = await Promise.all([
          api.get(`/calendar/entries?${qs.toString()}`),
          api.get("/vehicles").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const vmap = new Map((vv.data || []).map((v) => [v.id, v]));
        const withVehicle = (ev.data || []).map((e) => ({
          ...e,
          vehicle: e.resource_id ? vmap.get(e.resource_id) : null,
        }));
        withVehicle.sort((a, b) => new Date(a.start) - new Date(b.start));
        setUpcoming(withVehicle.slice(0, 5));
      } catch (err) {
        console.error("dashboard: upcoming bookings fetch failed", err);
      } finally {
        if (alive) setUpcomingLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [canSeeCalendar]);

  const launch = (app) => {
    if (app.embeddable) {
      navigate(`/apps/${app.id}/embed`);
    } else {
      openAppNewTab(app);
    }
  };

  const recentApps = [...apps].sort(compareApps).slice(0, 4);
  const hasAnyRecent = apps.some((a) => !!a.last_launched_at);

  return (
    <div data-testid="dashboard" className="mx-auto w-full max-w-6xl space-y-10">
      {/* Hero / greeting */}
      <section className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center">
        <div>
          <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            Welcome back
          </div>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {user?.full_name ? `Hi, ${user.full_name.split(" ")[0]}.` : "Hi."}{" "}
            <span className="text-cargodash-primary-600">Here's your hub.</span>
          </h2>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            Operational snapshot and fast access to your {activeTenant?.name || "tenant"} tools.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
          <span className="h-1.5 w-1.5 rounded-full bg-cargodash-primary-500" />
          Session secure · cd_session
        </div>
      </section>

      {/* KPI row */}
      <section data-testid="kpi-row">
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">
            Operations overview
          </h3>
          {kpiSource === "mock" && (
            <span className="text-[11px] text-slate-400">
              Source: mock · real data soon
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {KPI_DEFS.map((def) => (
            <KpiCard
              key={def.key}
              def={def}
              value={kpis ? kpis[def.key] ?? 0 : 0}
              loading={kpiLoading}
              isMock={kpiSource === "mock"}
            />
          ))}
        </div>
      </section>

      {/* Upcoming vehicle bookings */}
      {canSeeCalendar && (
        <section data-testid="upcoming-bookings">
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">
              Upcoming vehicle bookings
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/calendar")}
              data-testid="dashboard-open-calendar"
              className="gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              Open calendar
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
          {upcomingLoading ? (
            <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white/60" />
          ) : upcoming.length === 0 ? (
            <div
              data-testid="upcoming-empty"
              className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div>No vehicle bookings in the next 7 days.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {upcoming.map((e) => {
                const start = new Date(e.start);
                const end = new Date(e.end);
                const dayLabel = start.toLocaleDateString(undefined, {
                  weekday: "short", month: "short", day: "numeric",
                });
                const timeLabel = e.all_day
                  ? "All day"
                  : `${start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
                return (
                  <button
                    key={e.id}
                    onClick={() => navigate(`/calendar?view=day`)}
                    data-testid={`upcoming-row-${e.id}`}
                    className="flex w-full items-center gap-4 px-4 py-3 text-left transition hover:bg-slate-50"
                  >
                    <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-cargodash-primary-50 text-cargodash-primary-700">
                      <span className="text-[10px] font-semibold uppercase tracking-wider">
                        {start.toLocaleDateString(undefined, { month: "short" })}
                      </span>
                      <span className="font-display text-sm font-semibold leading-none">
                        {start.getDate()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{e.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                        <span>{dayLabel}</span>
                        <span className="text-slate-300">·</span>
                        <span>{timeLabel}</span>
                        {e.vehicle && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1 font-mono">
                              <Truck className="h-3 w-3" />
                              {e.vehicle.registration}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Recently used + Activity */}
      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">
              {hasAnyRecent ? "Recently used" : "Your apps"}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/apps")}
              data-testid="dashboard-all-apps"
              className="gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              All apps
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
          {appsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-xl border border-slate-200 bg-white/60"
                />
              ))}
            </div>
          ) : recentApps.length === 0 ? (
            <div
              data-testid="dashboard-apps-empty"
              className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500"
            >
              You don't have access to any apps yet. Ask an admin.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {recentApps.map((app) => (
                <AppTile key={app.id} app={app} onClick={launch} />
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-3 font-display text-lg font-semibold tracking-tight text-slate-900">
            Activity
          </h3>
          {activity.length === 0 ? (
            <div
              data-testid="activity-empty"
              className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <Activity className="h-5 w-5" />
              </div>
              <div className="mt-3 text-sm font-medium text-slate-700">No recent activity</div>
              <div className="mt-1 text-xs text-slate-400">
                {activityScope === "all" ? "Nothing yet across the hub." : "Your actions will show here."}
              </div>
            </div>
          ) : (
            <div
              data-testid="activity-feed"
              className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white"
            >
              {activity.map((r) => (
                <div
                  key={r.id}
                  data-testid={`activity-row-${r.id}`}
                  className="flex items-start gap-3 px-4 py-2.5"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cargodash-primary-500" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-slate-700">{formatActivity(r)}</div>
                    <div className="text-[11px] text-slate-400">{timeAgo(r.ts)}</div>
                  </div>
                </div>
              ))}
              {activityScope === "all" && (
                <div className="border-t border-slate-100 px-4 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/reports")}
                    className="gap-1 text-xs text-slate-500 hover:text-slate-700"
                  >
                    Full log <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
